/* The premiere card. A DIFFERENT OBJECT FROM AN ARCHIVE CARD, drawn to look it.
 *
 * An episode card is a square sleeve with a title under it, four across, and the
 * facts on it are the ones a two-hour broadcast has: a host roster, a runtime, a
 * tracklist flag. A premiere has none of those. It is ONE RECORD, so the facts
 * that matter are the artist, the track, the label and the release date — and
 * the release date matters most, because for the artist the whole point of doing
 * a premiere is the sentence "out 14 Sep on Flytree" arriving in front of an
 * audience before the record does.
 *
 * So this card is landscape rather than square: sleeve at the left, the artist
 * and title stacked beside it, and the release line as a FIELD ACROSS THE FOOT
 * of the card with a 3px rule over it. It is the loudest thing on the card after
 * the title, which is the correct order of importance and not a coincidence.
 *
 * The structure is the archive card's, for the reason given at the top of
 * EpisodeCardLink.tsx: a <div> with ONE stretched <a> on the title. Wrapping the
 * whole card in an anchor puts the genre links inside a link, which is invalid
 * HTML that browsers repair differently, and the chips stop working on some of
 * them. `.cardwrap`/`.card` also carry the whole motion vocabulary — lift to
 * (-3,-3) with the shadow growing, slam to (3,3) with it gone, 100ms linear —
 * so a new card inherits it rather than inventing a second one.
 *
 * Artwork is a palette field plus type. NEVER an <img> of supplied art: a wall
 * of other people's cover images in every resolution and taste destroys a flat
 * seven-colour palette by the twentieth record. See components/Artwork.tsx.
 */

import { Link } from 'react-router-dom';
import type { CSSProperties } from 'react';

import { formatFullDate } from '../format';
import type { TzMode } from '../format';
/* The wire type and this component share a name — the type mirrors the server's
   `PremiereCard` schema (see the note at the top of types.ts) and the component
   is what the barrel exports under that name. Aliased here, not renamed there. */
import type { PremiereCard as PremiereData } from '../types';
import { premiereCatalogueNo } from '../types';
import { Artwork } from './Artwork';
import { Chip, ChipList } from './Chip';
import { shortDate } from './shared';

/**
 * `Out 14 Sep 26 on Flytree Records`, or `Out now on Flytree Records`.
 *
 * THE TWO HALVES OF THIS SENTENCE ARE NOT INTERCHANGEABLE WORDING. Before the
 * release date the recording has never been published or made available, so the
 * performers' exclusive right is live and their direct consent is the whole
 * basis for us playing it; from the release date the recording is published, a
 * statutory remuneration right attaches (art. 7(2) WNR, §78(1) UrhG), and the
 * premiere is no longer the structurally clean thing it was — see the note above
 * `PremiereCard` in types.ts. `is_unreleased` is the server's answer to that
 * question and never a date comparison here, because a record can be made
 * available online without its release date moving.
 *
 * Falls back to "Out soon" rather than printing an em dash into the middle of a
 * sentence, for the premiere whose date the label has not fixed yet.
 */
export function releaseLine(
  premiere: PremiereData,
  tz: TzMode,
  form: 'short' | 'long' = 'short',
): string {
  const label = (premiere.label_name || '').trim();
  const on = label ? ` on ${label}` : '';

  if (!premiere.is_unreleased) return `Out now${on}`;

  const dated = Boolean(premiere.release_date && premiere.release_date.trim());
  if (!dated) return `Out soon${on}`;

  const when =
    form === 'long'
      ? formatFullDate(premiere.release_date, tz)
      : shortDate(premiere.release_date, tz);
  return `Out ${when}${on}`;
}

/* ------------------------------------------------------------------ styles */

/** Sleeve left, words right. The card's own flex direction is column in base.css. */
const ROW: CSSProperties = { flexDirection: 'row' };

/* The sleeve stretches to the card's height instead of holding 1:1, so two
   cards side by side line up on both edges however long a track title runs. */
const SLEEVE_ROW: CSSProperties = {
  width: 'clamp(124px, 22%, 168px)',
  flexShrink: 0,
  aspectRatio: 'auto',
  borderBottom: 0,
  borderRight: 'var(--bw) solid var(--edge)',
};

const BODY: CSSProperties = {
  padding: '13px 16px 0',
  gap: '8px',
  flexGrow: 1,
  minWidth: 0,
};

/** The foot of the card. Full-bleed, so the negative margins match BODY's padding. */
const RELEASE: CSSProperties = {
  marginTop: 'auto',
  marginLeft: '-16px',
  marginRight: '-16px',
  padding: '11px 16px 12px',
  borderTop: 'var(--bw) solid var(--edge)',
  background: 'var(--surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
};

const RELEASE_WORDS: CSSProperties = {
  fontSize: '1.0625rem',
  lineHeight: 1.05,
  letterSpacing: '-.005em',
  fontVariationSettings: "'wdth' 96, 'wght' 800",
  textTransform: 'uppercase',
  overflowWrap: 'anywhere',
};

const TITLE: CSSProperties = {
  fontSize: 'clamp(1.1875rem, 2.1vw, 1.5rem)',
  lineHeight: 1,
};

/* -------------------------------------------------------------------- card */

export interface PremiereCardProps {
  premiere: PremiereData;
  tz: TzMode;
  /**
   * Sleeve above the words instead of beside them. Passed down rather than
   * decided by a media query: this is an inline-styled card, and the page
   * already knows how many columns it is drawing (`useNarrow`).
   */
  stacked?: boolean;
  /**
   * Given, the card grows a play button. Without it the card is a link and
   * nothing else — right in a list nobody can play from.
   */
  onPlay?: (premiere: PremiereData) => void;
  /** This premiere is the one loaded in the persistent player, and running. */
  isPlaying?: boolean;
  /** Waiting on the detail fetch that the play button kicked off. */
  isStarting?: boolean;
  /** Routes a genre chip. Without it the chips are labels, not links. */
  genreHref?: (slug: string) => string;
  className?: string;
}

export function PremiereCard({
  premiere,
  tz,
  stacked = false,
  onPlay,
  isPlaying = false,
  isStarting = false,
  genreHref,
  className,
}: PremiereCardProps) {
  const catalogue = premiereCatalogueNo(premiere);
  const aired = `Premiered ${shortDate(premiere.premieres_at, tz)}`;

  return (
    <div className={className ? `cardwrap ${className}` : 'cardwrap'}>
      <div className="card" style={stacked ? undefined : ROW}>
        <Artwork
          color={premiere.artwork_color}
          catalogueNo={catalogue}
          label="Premiere"
          size="card"
          /* Stacked needs nothing: `.card .art` in base.css already puts the
             3px rule under a sleeve that sits above the words. */
          style={stacked ? undefined : SLEEVE_ROW}
        />

        <div className="card__body" style={BODY}>
          {/* The artist above the track, both in text: on a premiere neither one
              is the metadata of the other, and the artist is what people scan a
              feed of records for. */}
          <div className="mono-13">{premiere.artist_name}</div>

          <h3 style={TITLE}>
            <Link className="card__link" to={`/premieres/${encodeURIComponent(premiere.slug)}`}>
              {premiere.track_title}
            </Link>
          </h3>

          {/* One chips row. The status chip is an ink field, which is not a
              genre colour anywhere in this system, so "unreleased" cannot be
              misread as a genre — and it is a WORD, so the state does not
              depend on anybody seeing the colour. */}
          {premiere.is_unreleased || premiere.genres.length > 0 ? (
            <ChipList label="This premiere">
              {premiere.is_unreleased ? <Chip color="ink">Unreleased</Chip> : null}
              {premiere.genres.map((genre) => (
                <Chip
                  key={genre.slug}
                  color={genre.color_token}
                  to={genreHref ? genreHref(genre.slug) : undefined}
                >
                  {genre.name}
                </Chip>
              ))}
            </ChipList>
          ) : null}

          <div style={RELEASE}>
            <div style={RELEASE_WORDS}>{releaseLine(premiere, tz)}</div>
            <div className="mono dim">
              {catalogue} · {aired}
            </div>
          </div>
        </div>
      </div>

      {onPlay ? (
        <button
          type="button"
          className="pbtn card__play"
          aria-label={`${isPlaying ? 'Pause' : 'Play'} ${premiere.artist_name}, ${premiere.track_title}`}
          aria-pressed={isPlaying}
          disabled={isStarting}
          onClick={() => onPlay(premiere)}
        >
          {/* Loading is a THIRD state and is drawn as one: the glyph would
              otherwise say "stopped" for as long as the detail fetch takes. */}
          {isStarting ? (
            <span className="mono" aria-hidden="true">
              ···
            </span>
          ) : (
            <span className={isPlaying ? 'pausebars' : 'tri'} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}
